import {
    Button,
    Checkbox,
    CircularProgress,
    DialogContentText,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Menu,
    IconButton,
} from "@mui/material";
import React, { ChangeEvent, useEffect, useState } from "react";
import axios from "../../../utils/axiosInstance";
import { useSnackbar } from "notistack";
import {
    ENVIRONMENTS,
    ERROR_HEADERS_CSV,
    FOLDER,
    S3_FOLDER_ENV_NAMES,
} from "../LandingPage/constants";
import FolderBreadcrumbs from "./breadcrumbs";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import GeneralInfoModal from "../../UI/atoms/Popup/GeneralInfoModal";
import DownloadIcon from "@mui/icons-material/Download";
import { CSVLink } from "react-csv";
import {
    ACTION,
    PERMISSIONS,
    usePermissions,
    ROLE,
} from "../../../utils/permissions";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import ExcelUploadButton from "./ExcelUploadButton";
import RestoreFromGlacierDialog from "./RestoreGlacier";
import WaivableErrorsDialog from "../../UI/atoms/Popup/WaivableErrorsDialog";
import { FILE_STATUS_COLORS } from "../../../constants/configuration.const";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SlateLogo from "../../../assets/imgs/Amazon_Prime_Video_logo.svg";
import RokuLogo from "../../../assets/imgs/Roku_logo.svg";
import CreateProxyDialog from "./CreateProxyDialog";

// Package generation is distributor-scoped. The backend defaults to Amazon/PVC and
// switches MEC/MMC schema versions, value mappings and file prefixes on ?distributor=roku.
type PackageDistributor = "amazon" | "roku" | "mdu";

// HTTP entry for Roku bulk copy (Step Function / Lambda); same body shape as Slate s3-copy.
const ROKU_S3_COPY_API_PATH = "api/v1/roku-copy";
// Local canvas (.env BASE_URL=localhost) hits Stage for Roku copy until local gateway exists.
const ROKU_S3_COPY_STAGE_BASE_URL = "https://api-stage.capradev.com/";

const getRokuCopyBaseUrl = () => {
    const base = process.env.BASE_URL || "";
    if (/localhost|127\.0\.0\.1/i.test(base)) {
        return ROKU_S3_COPY_STAGE_BASE_URL;
    }
    return base.endsWith("/") ? base : `${base}/`;
};

const PACKAGE_ACTION_LABEL = {
    amazon: "PVC Package",
    roku: "Roku Package",
    mdu: "MDU Packages",
} as const;

const PACKAGE_SHORT_NAME = {
    amazon: "PVC",
    roku: "Roku",
    mdu: "MDU",
} as const;

const S3_EXTENSION_FILTERS = {
    all: { label: "All files", extensions: null as string[] | null },
    images: {
        label: "Images",
        extensions: [".jpg", ".jpeg", ".png", ".gif"],
    },
    videos: { label: "Videos", extensions: [".mov", ".mp4"] },
    subtitles: { label: "Subtitles", extensions: [".srt"] },
    xml: { label: "XML", extensions: [".xml"] },
    xlsx: { label: "XLSX", extensions: [".xlsx"] },
} as const;

type S3ExtensionFilterKey = keyof typeof S3_EXTENSION_FILTERS;

const matchesExtensionFilter = (
    row: any,
    filterKey: S3ExtensionFilterKey
) => {
    if (filterKey === "all") return true;
    if (row?.type === FOLDER || String(row?.name || "").endsWith("/"))
        return true;
    const name = String(row?.name || "").toLowerCase();
    const extensions = S3_EXTENSION_FILTERS[filterKey].extensions || [];
    return extensions.some((ext) => name.endsWith(ext));
};

const BrowseS3List = ({ ingestionStart }: any) => {
    const [s3ObjectList, sets3ObjectList] = useState<any[]>([]);
    const [extensionFilter, setExtensionFilter] =
        useState<S3ExtensionFilterKey>("all");
    const [s3Prefix, setS3Prefix] = useState("");
    const [loader, setLoader] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [, setIsPopupOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<any>(null);
    const [loadMoreLoader, setloadMoreLoader] = useState(false);
    const [nextToken, setNextToken] = useState<any>(null);
    const [isValidatePopupOpen, setIsValidatePopupOpen] = useState(false);
    const [validationErrors, setvalidationErrors] = useState<any>(null);
    const [validateLoader, setValidateLoader] = useState(false);
    const [, setIsVideoProfilePopupOpen] = useState(false);
    const [selectedProfile, setSelectedProfile] = useState<any>(null);
    const [validatedFile, setValidatedFile] = useState<string[]>([]);
    const { hasPermission } = usePermissions();
    const [mediaInfoData, setMediaInfoData] = useState<any>(null);
    const [mediaInfoDialogOpen, setMediaInfoDialogOpen] = useState(false);
    const [moveToGlacierLoader, setMoveToGlacierLoader] = useState(false);
    const [restoreGlacierDialogOpen, setRestoreGlacierDialogOpen] =
        useState(false);
    /** When set, restore uses this path (folder) instead of checkbox selection. */
    const [restoreS3Path, setRestoreS3Path] = useState<string | null>(null);
    const [glacierConfirmOpen, setGlacierConfirmOpen] = React.useState(false);
    const [waivableErrorsDialogOpen, setWaivableErrorsDialogOpen] =
        useState(false);
    const [waivableErrorsData, setWaivableErrorsData] = useState<{
        errors: string[];
        action: "Validate" | "PVC Package" | "Roku Package" | "MDU Packages";
    } | null>(null);
    const [pendingXMLParams, setPendingXMLParams] = useState<{
        studioName: string;
        fileNameValue: string;
        actualKey: any;
        distributor: PackageDistributor;
    } | null>(null);
    const [glacierMenuAnchor, setGlacierMenuAnchor] =
        useState<null | HTMLElement>(null);
    const [proxyDialogOpen, setProxyDialogOpen] = useState(false);
    const [proxyTargetRow, setProxyTargetRow] = useState<any>(null);
    const [proxyHd, setProxyHd] = useState(false);
    const [proxySubmitting, setProxySubmitting] = useState(false);

    let isDev = process.env.APP_ENVIRONMENT === ENVIRONMENTS.DEV;
    if (process.env.APP_ENVIRONMENT === "STAGE") {
        const savedEnv = localStorage.getItem("lastSelectedEnv") || "PROD";
        isDev = savedEnv === "DEV";
    }
    const [currents3Env, setCurrents3Env] = useState(
        isDev ? ENVIRONMENTS.DEV : ENVIRONMENTS.PROD
    );
    const filteredS3ObjectList = s3ObjectList.filter((row) =>
        matchesExtensionFilter(row, extensionFilter)
    );

    const selectableKeys = filteredS3ObjectList
        .filter(
            (row) =>
                row.type !== FOLDER && row.fileStatus !== "restore-in-progress"
        )
        .map((row) => ({ key: row.actualKey, fileStatus: row.fileStatus }));

    const [allSelectedKeys, setAllSelectedKeys] = useState<
        { key: string; fileStatus: string }[]
    >([]);

    const selectedValidKeysForCopy = allSelectedKeys
        .filter((k) => ["standard", "restored"].includes(k.fileStatus))
        .map((k) => k.key);
    const selectedValidKeysForGlacierMove = allSelectedKeys
        .filter((k) => k.fileStatus === "standard")
        .map((k) => k.key);
    const selectedValidKeysForRestore = allSelectedKeys
        .filter((k) => k.fileStatus === "archived")
        .map((k) => k.key);

    const isAllSelected =
        allSelectedKeys.length > 0 &&
        allSelectedKeys.length === selectableKeys.length;
    useEffect(() => {
        setAllSelectedKeys([]);
        setExtensionFilter("all");
        const savedPath = localStorage.getItem("lastVisitedS3Path") || "";
        gets3Objects(savedPath, false);
    }, [currents3Env]);

    const gets3Objects = async (
        filePath: string = s3Prefix,
        dataAppendAction: boolean = true
    ) => {
        if (!dataAppendAction) {
            setExtensionFilter("all");
        }
        setLoader(true);
        try {
            const s3objectresponse = await axios.get(
                `${
                    process.env.BASE_URL
                }api/v1/cms/ingest/s3-browse?s3Path=${encodeURIComponent(
                    filePath
                )}&s3Env=${encodeURIComponent(currents3Env)}&nextToken=${
                    (nextToken &&
                        dataAppendAction &&
                        encodeURIComponent(nextToken)) ||
                    ""
                }`
            );
            if (s3objectresponse.data?.success) {
                let resultList = s3objectresponse.data?.data?.list || [];
                resultList = resultList.map((i: Record<string, any>) => {
                    let fileStatus:
                        | "archived"
                        | "restore-in-progress"
                        | "restored"
                        | "standard" = "standard";
                    if (i.storageClass !== "STANDARD" && i.type !== FOLDER) {
                        fileStatus = "archived";
                    }
                    if (i.restore?.status) {
                        if (i.restore.status === "in-progress") {
                            fileStatus = "restore-in-progress";
                        }
                        if (i.restore.status === "restored") {
                            fileStatus = "restored";
                        }
                    }

                    return {
                        ...i,
                        fileStatus,
                    };
                });
                if (nextToken && dataAppendAction) {
                    sets3ObjectList([...s3ObjectList, ...resultList]);
                } else {
                    sets3ObjectList(resultList || []);
                }
                const currentPrefix =
                    s3objectresponse.data?.data?.currentPrefix || "";

                setS3Prefix(s3objectresponse.data?.data?.currentPrefix || "");
                setNextToken(s3objectresponse.data?.data?.nextToken);

                localStorage.setItem("lastVisitedS3Path", currentPrefix);
            }
        } catch (error: any) {
            enqueueSnackbar(error?.response?.data?.message, {
                variant: "error",
            });
        } finally {
            setLoader(false);
        }
    };

    const onClickFolder = (row: any) => {
        if (row?.type == FOLDER && row?.actualKey) {
            setAllSelectedKeys([]);
            setExtensionFilter("all");
            gets3Objects(row.actualKey, false);
        }
    };

    // Select / deselect all checkboxes
    const handleSelectAll = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) setAllSelectedKeys(selectableKeys);
        else setAllSelectedKeys([]);
    };

    // Select / deselect a single row
    const handleSelectOne =
        (row: Record<string, string>) => (e: ChangeEvent<HTMLInputElement>) => {
            if (e.target.checked)
                setAllSelectedKeys((prev) => [
                    ...prev,
                    { key: row.actualKey, fileStatus: row.fileStatus },
                ]);
            else
                setAllSelectedKeys((prev) =>
                    prev.filter((k) => k.key !== row.actualKey)
                );
        };

    // Bulk copy to Slate
    const handleCopyToSlateBulk = () => {
        performS3Copy(selectedValidKeysForCopy, "api/v1/s3-copy");
        setAllSelectedKeys([]);
    };

    // Bulk copy to Roku (same selection rules as Slate; Stage base when developing locally)
    const handleCopyToRokuBulk = () => {
        performS3Copy(
            selectedValidKeysForCopy,
            ROKU_S3_COPY_API_PATH,
            getRokuCopyBaseUrl()
        );
        setAllSelectedKeys([]);
    };

    const handleGlacierMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setGlacierMenuAnchor(event.currentTarget);
    };

    const handleGlacierMenuClose = () => {
        setGlacierMenuAnchor(null);
    };

    const handleMoveToGlacierClick = () => {
        handleGlacierMenuClose();
        setGlacierConfirmOpen(true);
    };

    const handleRestoreClick = () => {
        handleGlacierMenuClose();
        setRestoreS3Path(null);
        setRestoreGlacierDialogOpen(true);
    };

    const openRestoreFromGlacier = (row: any) => {
        setRestoreS3Path(row?.actualKey || null);
        setRestoreGlacierDialogOpen(true);
    };

    const ingestionIntiate = (
        validate = false,
        fileName = null,
        actualKey = null
    ) => {
        if (s3Prefix) {
            const prefixArray = s3Prefix.split("/");
            const studioName = prefixArray[0];
            const sliced = prefixArray.slice(1);
            const filePath = sliced.join("/") + (fileName || selectedFile);
            if (validate) {
                validationAPI(studioName, filePath, actualKey);
            } else {
                ingestionStart(
                    studioName,
                    filePath,
                    selectedProfile,
                    currents3Env
                );
                setIsVideoProfilePopupOpen(false);
                setSelectedProfile(null);
            }
            setIsPopupOpen(false);
        } else {
            enqueueSnackbar("Please select studio", { variant: "warning" });
        }
    };

    const xmlInitiate = (
        fileName = null,
        actualKey = null,
        distributor: PackageDistributor = "amazon"
    ) => {
        if (s3Prefix) {
            const prefixArray = s3Prefix.split("/");
            const studioName = prefixArray[0];
            const sliced = prefixArray.slice(1);
            const filePath = sliced.join("/") + (selectedFile || fileName);

            createXMLAPI(studioName, filePath, actualKey, false, distributor);
            setSelectedProfile(null);
        } else {
            enqueueSnackbar("Please select studio", { variant: "warning" });
        }
    };

    const loadMore = async () => {
        try {
            setloadMoreLoader(true);
            await gets3Objects();
        } catch {
            // gets3Objects already surfaces the failure to the user.
        } finally {
            setloadMoreLoader(false);
        }
    };

    const ingestionValidatePopupOpen = (row: any) => {
        setIsValidatePopupOpen(true);
        setvalidationErrors(row);
    };

    const ingestionValidatePopupClose = () => {
        setIsValidatePopupOpen(false);
    };

    const validateExcel = (row: any) => {
        setSelectedFile(row?.name);
        ingestionIntiate(true, row?.name, row?.actualKey);
    };

    const validationAPI = async (
        studioName: string,
        fileNameValue: string,
        actualKey: any = null
    ) => {
        try {
            if (studioName && fileNameValue) {
                const xlsxFileName: any = fileNameValue.includes("xlsx")
                    ? fileNameValue
                    : fileNameValue + ".xlsx";
                setValidateLoader(true);
                const validationAPIResponse = await axios.get(
                    `${process.env.BASE_URL}api/v1/cms/ingest/validate?studioName=${encodeURIComponent(
                        studioName
                    )}&fileName=${encodeURIComponent(
                        xlsxFileName
                    )}&s3Env=${encodeURIComponent(currents3Env)}`
                );
                const validationPayload = validationAPIResponse.data?.data;
                const validationErrors = Array.isArray(validationPayload)
                    ? validationPayload
                    : [];

                // Check for WAIVABLE_ERRORS format
                if (
                    validationAPIResponse.data?.errorCode === "WAIVABLE_ERRORS"
                ) {
                    // Store the parameters for retry with waiveErrors (if validate endpoint supports it)
                    setPendingXMLParams({
                        studioName,
                        fileNameValue,
                        actualKey,
                        distributor: "amazon",
                    });
                    // Transform allErrors array of objects to array of formatted strings
                    const formattedErrors = (
                        validationAPIResponse.data?.data?.allErrors || []
                    ).map((errorItem: any) => {
                        if (typeof errorItem === "string") {
                            return errorItem;
                        }
                        const errorMessages = Array.isArray(
                            errorItem.errorMessage
                        )
                            ? errorItem.errorMessage.join(", ")
                            : errorItem.errorMessage || "";
                        return errorItem.rowNo
                            ? `Row ${errorItem.rowNo}: ${errorMessages}`
                            : errorMessages;
                    });
                    setWaivableErrorsData({
                        errors: formattedErrors,
                        action: "Validate",
                    });
                    setWaivableErrorsDialogOpen(true);
                }
                // INCASE IF ANY VALIDATION ERRORS
                if (
                    validationAPIResponse.data?.success &&
                    validationErrors.length > 0
                ) {
                    ingestionValidatePopupOpen({
                        errors: validationErrors,
                    });
                    removeActualKey(actualKey);
                }
                // Validation started in background or completed with no errors
                else if (validationAPIResponse.data?.success) {
                    enqueueSnackbar(
                        validationAPIResponse.data?.message ||
                            "Validation successful",
                        { variant: "success" }
                    );
                    if (Array.isArray(validationPayload)) {
                        addActualKey(actualKey);
                    }
                } else {
                    enqueueSnackbar(
                        validationAPIResponse.data?.message ||
                            "An error occurred",
                        { variant: "error" }
                    );
                    removeActualKey(actualKey);
                }
            } else {
                enqueueSnackbar("Studio Name & File Name are mandatory", {
                    variant: "warning",
                });
            }
        } catch (error: any) {
            enqueueSnackbar(
                error?.response?.data?.message || "An error occurred",
                { variant: "error" }
            );
            removeActualKey(actualKey);
        } finally {
            setValidateLoader(false);
            setSelectedFile(null);
        }
    };

    const createXMLAPI = async (
        studioName: string,
        fileNameValue: string,
        actualKey: any = null,
        waiveErrors: boolean = false,
        distributor: PackageDistributor = "amazon"
    ) => {
        try {
            if (studioName && fileNameValue) {
                const xlsxFileName: any = fileNameValue.includes("xlsx")
                    ? fileNameValue
                    : fileNameValue + ".xlsx";
                const url = `${
                    process.env.BASE_URL
                }api/v1/cms/ingest/create-xml?studioName=${encodeURIComponent(
                    studioName
                )}&fileName=${encodeURIComponent(
                    xlsxFileName
                )}&s3Env=${encodeURIComponent(currents3Env)}${
                    waiveErrors ? "&waiveErrors=true" : ""
                }${distributor === "roku" ? "&distributor=roku" : ""}${
                    distributor === "mdu" ? "&workflow=mdu" : ""
                }`;
                const xmlAPIResponse = await axios.get(url);

                if (xmlAPIResponse.data?.errorCode === "WAIVABLE_ERRORS") {
                    // Store the parameters for retry with waiveErrors
                    setPendingXMLParams({
                        studioName,
                        fileNameValue,
                        actualKey,
                        distributor,
                    });
                    // Transform allErrors array of objects to array of formatted strings
                    const formattedErrors = (
                        xmlAPIResponse.data?.data?.allErrors || []
                    ).map((errorItem: any) => {
                        if (typeof errorItem === "string") {
                            return errorItem;
                        }
                        const errorMessages = Array.isArray(
                            errorItem.errorMessage
                        )
                            ? errorItem.errorMessage.join(", ")
                            : errorItem.errorMessage || "";
                        return errorItem.rowNo
                            ? `Row ${errorItem.rowNo}: ${errorMessages}`
                            : errorMessages;
                    });
                    setWaivableErrorsData({
                        errors: formattedErrors,
                        action: PACKAGE_ACTION_LABEL[distributor],
                    });
                    setWaivableErrorsDialogOpen(true);
                } else if (xmlAPIResponse.data?.success) {
                    enqueueSnackbar(
                        `${PACKAGE_SHORT_NAME[distributor]} Package Generation Request is Submitted. Please check Package Logs for the status`,
                        { variant: "success" }
                    );
                } else {
                    enqueueSnackbar(
                        xmlAPIResponse.data?.message || "An error occurred",
                        { variant: "error" }
                    );
                    removeActualKey(actualKey);
                }
            } else {
                enqueueSnackbar("Something went wrong", { variant: "warning" });
            }
        } catch (error: any) {
            if (error?.response?.data?.errorCode === "WAIVABLE_ERRORS") {
                // Store the parameters for retry with waiveErrors
                setPendingXMLParams({
                    studioName,
                    fileNameValue,
                    actualKey,
                    distributor,
                });
                // Transform allErrors array of objects to array of formatted strings
                const formattedErrors = (
                    error.response.data?.data?.allErrors || []
                ).map((errorItem: any) => {
                    if (typeof errorItem === "string") {
                        return errorItem;
                    }
                    const errorMessages = Array.isArray(errorItem.errorMessage)
                        ? errorItem.errorMessage.join(", ")
                        : errorItem.errorMessage || "";
                    return errorItem.rowNo
                        ? `Row ${errorItem.rowNo}: ${errorMessages}`
                        : errorMessages;
                });
                setWaivableErrorsData({
                    errors: formattedErrors,
                    action: PACKAGE_ACTION_LABEL[distributor],
                });
                setWaivableErrorsDialogOpen(true);
            } else {
                enqueueSnackbar(
                    error?.response?.data?.message || "An error occurred",
                    { variant: "error" }
                );
                removeActualKey(actualKey);
            }
        } finally {
            setValidateLoader(false);
            setSelectedFile(null);
        }
    };

    const handleWaiveErrors = () => {
        if (pendingXMLParams) {
            setWaivableErrorsDialogOpen(false);
            createXMLAPI(
                pendingXMLParams.studioName,
                pendingXMLParams.fileNameValue,
                pendingXMLParams.actualKey,
                true, // waiveErrors = true
                pendingXMLParams.distributor
            );
            setPendingXMLParams(null);
        }
    };

    const handleCloseWaivableErrorsDialog = () => {
        setWaivableErrorsDialogOpen(false);
        setWaivableErrorsData(null);
        if (pendingXMLParams) {
            removeActualKey(pendingXMLParams.actualKey);
            setPendingXMLParams(null);
        }
    };

    const openCreateProxy = (row: any) => {
        setProxyTargetRow(row);
        setProxyHd(false);
        setProxyDialogOpen(true);
    };

    const closeCreateProxy = () => {
        if (proxySubmitting) return;
        setProxyDialogOpen(false);
        setProxyTargetRow(null);
        setProxyHd(false);
    };

    const createProxyAPI = async () => {
        if (!proxyTargetRow?.actualKey) return;
        setProxySubmitting(true);
        try {
            const response = await axios.post(
                `${process.env.BASE_URL}api/v1/cms/ingest/proxy`,
                {
                    sourceKey: proxyTargetRow.actualKey,
                    s3Env: currents3Env,
                    hd: proxyHd,
                }
            );
            if (response.data?.success) {
                enqueueSnackbar(
                    response.data?.message ||
                        "Proxy request is being processed",
                    { variant: "success" }
                );
                setProxyDialogOpen(false);
                setProxyTargetRow(null);
                setProxyHd(false);
            } else {
                enqueueSnackbar(
                    response.data?.message || "Failed to start proxy job",
                    { variant: "error" }
                );
            }
        } catch (error: any) {
            enqueueSnackbar(
                error?.response?.data?.message ||
                    "Failed to start proxy job",
                { variant: "error" }
            );
        } finally {
            setProxySubmitting(false);
        }
    };

    const getMediaInfoAPI = async (actualKey: any = null) => {
        try {
            const selectedEnv = S3_FOLDER_ENV_NAMES.find(
                (role) => role.envName === currents3Env
            );

            const selectedS3Bucket = selectedEnv?.envLabel;

            const mediaInfoAPIResponse = await axios.post(
                `${process.env.BASE_URL}api/v1/media-info`,
                { s3_key: actualKey, bucket_name: selectedS3Bucket }
            );
            // INCASE IF ANY VALIDATION ERRORS
            if (mediaInfoAPIResponse.data?.message) {
                setMediaInfoData(mediaInfoAPIResponse.data.message); // save data
                setMediaInfoDialogOpen(true); // open popup
                enqueueSnackbar("Media Info generated successfully", {
                    variant: "success",
                });
            } else {
                enqueueSnackbar(mediaInfoAPIResponse.data?.error, {
                    variant: "error",
                });
            }
        } catch (error: any) {
            enqueueSnackbar(error?.response?.data?.message, {
                variant: "error",
            });
            removeActualKey(actualKey);
        } finally {
            setValidateLoader(false);
            setSelectedFile(null);
        }
    };

    const performS3Copy = async (
        actualKey: any = null,
        apiPath = "api/v1/s3-copy",
        baseUrl = process.env.BASE_URL
    ) => {
        try {
            const selectedEnv = S3_FOLDER_ENV_NAMES.find(
                (role) => role.envName === currents3Env
            );

            const selectedS3Bucket = selectedEnv?.envLabel;

            enqueueSnackbar("File copy request submitted", {
                variant: "success",
            });

            axios
                .post(`${baseUrl}${apiPath}`, {
                    source_key: actualKey,
                    bucket_name: selectedS3Bucket,
                })
                .catch(() => {
                    removeActualKey(actualKey);
                });
        } catch {
            removeActualKey(actualKey);
        } finally {
            setValidateLoader(false);
            setSelectedFile(null);
        }
    };

    const performGlacierCopy = async (actualKey: any = null) => {
        try {
            // Send path in body so keys with '#' are not truncated as URL fragments.
            const glacierCopyResponse = await axios.post(
                `${process.env.BASE_URL}api/v1/cms/ingest/glacier`,
                { s3Path: actualKey, s3Env: currents3Env }
            );
            if (glacierCopyResponse.data?.success) {
                enqueueSnackbar(
                    "Glacier Process Started. Please verify the status in sometime",
                    { variant: "success" }
                );
            } else {
                enqueueSnackbar(glacierCopyResponse.data?.message, {
                    variant: "error",
                });
            }
        } catch {
            removeActualKey(actualKey);
        } finally {
            setValidateLoader(false);
            setSelectedFile(null);
        }
    };

    const performMultiGlacierCopy = async (keys: string[] = []) => {
        try {
            setMoveToGlacierLoader(true);
            const glacierCopyResponse = await axios.post(
                `${process.env.BASE_URL}api/v1/cms/ingest/glacier`,
                { files: keys, s3Env: currents3Env }
            );
            if (glacierCopyResponse.data?.success) {
                enqueueSnackbar(
                    "Glacier Process Started. Please verify the status in sometime",
                    { variant: "success" }
                );
            } else {
                enqueueSnackbar(glacierCopyResponse.data?.message, {
                    variant: "error",
                });
            }
            setMoveToGlacierLoader(false);
            setGlacierConfirmOpen(false);
        } catch (error) {
            console.error("Error during multi glacier copy:", error);
        }
    };

    const addActualKey = (key: string) => {
        setValidatedFile(Array.from(new Set([...validatedFile, key])));
    };

    const removeActualKey = (key: string) => {
        const actualKey = [...validatedFile];
        const indexToRemove = actualKey.indexOf(key);
        if (indexToRemove !== -1) {
            actualKey.splice(indexToRemove, 1);
        }
    };

    const errorMessagFormattingForCSV = (errors: any[]) => {
        let errorsMessages: any[] = [];
        for (const err of errors) {
            errorsMessages.push({
                rowNo: err.rowNo,
                errorMessage: err.errorMessage.join("\n"),
            });
        }
        return errorsMessages;
    };
    const generateXml = async (
        row: any,
        distributor: PackageDistributor = "amazon"
    ) => {
        setSelectedFile(row?.name);
        xmlInitiate(row?.name, row?.actualKey, distributor);
    };

    const showMediaInfo = async (row: any) => {
        setSelectedFile(row?.actualKey);
        getMediaInfoAPI(row?.actualKey);
    };

    const { hasRole } = usePermissions();

    const moveToGlacier = async (row: any) => {
        setSelectedFile(row?.actualKey);
        performGlacierCopy(row?.actualKey);
    };

    const navigateToPrefix = (prefix: string) => {
        setAllSelectedKeys([]);
        setExtensionFilter("all");
        gets3Objects(prefix, false);
    };

    const downloadFile = async (row: any) => {
        setLoader(true);
        try {
            const prefixArray = s3Prefix.split("/");
            const studioName = prefixArray[0];
            const sliced = prefixArray.slice(1);
            const filePath = sliced.join("/") + row?.name;
            const response = await axios.get(
                `${process.env.BASE_URL}api/v1/cms/ingest/download-template?studioName=${encodeURIComponent(
                    studioName
                )}&fileName=${encodeURIComponent(
                    filePath
                )}&s3Env=${encodeURIComponent(currents3Env)}`
            );
            if (response.status !== 200) {
                enqueueSnackbar("File download failed", { variant: "error" });
                return;
            }

            window.location.href = response.data.data.url;
        } catch {
            enqueueSnackbar("An error occurred while downloading the file", {
                variant: "error",
            });
        } finally {
            setLoader(false);
        }
    };

    const handleRestore = async (days: number) => {
        const isPathRestore = Boolean(restoreS3Path);
        try {
            const restoreGlacierResponse = await axios.post(
                `${process.env.BASE_URL}api/v1/cms/ingest/restore-from-glacier`,
                isPathRestore
                    ? {
                          s3Path: restoreS3Path,
                          s3Env: currents3Env,
                          days,
                      }
                    : {
                          files: selectedValidKeysForRestore,
                          s3Env: currents3Env,
                          days,
                      }
            );
            if (restoreGlacierResponse.data?.success) {
                enqueueSnackbar(
                    "Glacier Restore Process Started. Please verify the status in sometime",
                    { variant: "success" }
                );
            } else {
                enqueueSnackbar(restoreGlacierResponse.data?.message, {
                    variant: "error",
                });
            }
            setRestoreGlacierDialogOpen(false);
            setRestoreS3Path(null);
        } catch (error) {
            console.error("Error during glacier restore:", error);
        }
    };

    const isGlacierMenuDisabled =
        !selectedValidKeysForGlacierMove.length &&
        !selectedValidKeysForRestore.length;

    return (
        <div>
            <div
                className="d-flex j-end a-center"
                style={{ height: "50px", margin: "10px 0px" }}
            >
                <FormControl sx={{ height: "100%" }}>
                    <InputLabel id="folder-label">S3 Bucket</InputLabel>
                    <Select
                        labelId="folder-label"
                        id="folder"
                        name="folder"
                        value={currents3Env}
                        label="s3-folder"
                        onChange={(e) => {
                            localStorage.setItem("lastVisitedS3Path", "");
                            localStorage.setItem(
                                "lastSelectedEnv",
                                e.target.value
                            );
                            setCurrents3Env(e.target.value);
                            setAllSelectedKeys([]);
                        }}
                        disabled={
                            !hasRole(ROLE.ADMIN) ||
                            process.env.APP_ENVIRONMENT != "STAGE"
                        }
                        sx={{ height: "100%" }}
                    >
                        {S3_FOLDER_ENV_NAMES.map(
                            (role: { envLabel: string; envName: string }) => (
                                <MenuItem
                                    key={role.envName}
                                    value={role.envName}
                                >
                                    {role.envLabel}
                                </MenuItem>
                            )
                        )}
                    </Select>
                </FormControl>
                <ExcelUploadButton
                    s3Prefix={s3Prefix}
                    currents3Env={currents3Env}
                    setLoader={setLoader}
                    enqueueSnackbar={enqueueSnackbar}
                    gets3Objects={gets3Objects}
                />
                {hasPermission(PERMISSIONS.ingestion, ACTION.EDIT) && (
                    <>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleCopyToSlateBulk}
                            disabled={!selectedValidKeysForCopy.length}
                            sx={{
                                "&.Mui-disabled img": {
                                    filter: "contrast(0.5)",
                                },
                                marginLeft: "10px",
                                height: "40px",
                                minHeight: "40px",
                                textTransform: "none",
                                whiteSpace: "nowrap",
                            }}
                        >
                            Copy to
                            <img
                                src={SlateLogo}
                                alt="Slate"
                                style={{
                                    height: "16px",
                                    width: "auto",
                                    maxHeight: "16px",
                                    marginLeft: "6px",
                                    marginRight: "4px",
                                    verticalAlign: "middle",
                                    display: "inline-block",
                                }}
                            />
                            ({selectedValidKeysForCopy.length})
                        </Button>

                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleCopyToRokuBulk}
                            disabled={!selectedValidKeysForCopy.length}
                            sx={{
                                "&.Mui-disabled img": {
                                    filter: "contrast(0.5)",
                                },
                                marginLeft: "10px",
                                height: "40px",
                                minHeight: "40px",
                                textTransform: "none",
                                whiteSpace: "nowrap",
                            }}
                        >
                            Copy to
                            <img
                                src={RokuLogo}
                                alt="Roku"
                                style={{
                                    height: "14px",
                                    width: "auto",
                                    maxHeight: "14px",
                                    marginLeft: "6px",
                                    marginRight: "4px",
                                    verticalAlign: "middle",
                                    display: "inline-block",
                                }}
                            />
                            ({selectedValidKeysForCopy.length})
                        </Button>

                        <IconButton
                            onClick={handleGlacierMenuOpen}
                            disabled={isGlacierMenuDisabled}
                            color="primary"
                        >
                            <MoreVertIcon />
                        </IconButton>

                        <Menu
                            anchorEl={glacierMenuAnchor}
                            open={Boolean(glacierMenuAnchor)}
                            onClose={handleGlacierMenuClose}
                        >
                            <MenuItem
                                onClick={handleMoveToGlacierClick}
                                disabled={
                                    !selectedValidKeysForGlacierMove.length ||
                                    moveToGlacierLoader
                                }
                            >
                                Archive (
                                {selectedValidKeysForGlacierMove.length})
                            </MenuItem>
                            <MenuItem
                                onClick={handleRestoreClick}
                                disabled={!selectedValidKeysForRestore.length}
                            >
                                Initiate Restore (
                                {selectedValidKeysForRestore.length})
                            </MenuItem>
                        </Menu>

                        <Dialog
                            open={glacierConfirmOpen}
                            onClose={() => setGlacierConfirmOpen(false)}
                        >
                            <DialogTitle>Confirm Move to Glacier</DialogTitle>
                            <DialogContent>
                                <DialogContentText>
                                    Are you sure you want to move{" "}
                                    {selectedValidKeysForGlacierMove.length}{" "}
                                    file
                                    {selectedValidKeysForGlacierMove.length !==
                                    1
                                        ? "s"
                                        : ""}{" "}
                                    to Glacier storage? This action will change
                                    the storage class of the selected files.
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={() => setGlacierConfirmOpen(false)}
                                    color="primary"
                                    disabled={moveToGlacierLoader}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={() => {
                                        performMultiGlacierCopy(
                                            selectedValidKeysForGlacierMove
                                        );
                                    }}
                                    color="primary"
                                    variant="contained"
                                    disabled={moveToGlacierLoader}
                                    autoFocus
                                >
                                    Confirm{" "}
                                    {moveToGlacierLoader && (
                                        <CircularProgress
                                            size={20}
                                            color="inherit"
                                            style={{ marginLeft: "8px" }}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Dialog>

                        <RestoreFromGlacierDialog
                            open={restoreGlacierDialogOpen}
                            onClose={() => {
                                setRestoreGlacierDialogOpen(false);
                                setRestoreS3Path(null);
                            }}
                            selectedFilesCount={
                                restoreS3Path
                                    ? 1
                                    : selectedValidKeysForRestore.length
                            }
                            onRestore={handleRestore}
                        />
                    </>
                )}
            </div>
            <div className="d-flex j-space" style={{ marginBottom: "10px" }}>
                <FolderBreadcrumbs
                    prefix={s3Prefix}
                    gets3Objects={navigateToPrefix}
                />
                <div className="d-flex a-center" style={{ gap: "12px" }}>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id="s3-extension-filter-label">
                            File type
                        </InputLabel>
                        <Select
                            labelId="s3-extension-filter-label"
                            id="s3-extension-filter"
                            label="File type"
                            value={extensionFilter}
onChange={(e) => {
    setAllSelectedKeys([]);
    setExtensionFilter(
        e.target.value as S3ExtensionFilterKey
    );
}}
                        >
                            {(
                                Object.keys(
                                    S3_EXTENSION_FILTERS
                                ) as S3ExtensionFilterKey[]
                            ).map((key) => (
                                <MenuItem key={key} value={key}>
                                    {S3_EXTENSION_FILTERS[key].label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {loader && (
                        <CircularProgress size={20} color="primary" />
                    )}
                </div>
            </div>
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    indeterminate={
                                        allSelectedKeys.length > 0 &&
                                        allSelectedKeys.length <
                                            selectableKeys.length
                                    }
                                    checked={isAllSelected}
                                    onChange={handleSelectAll}
                                />
                            </TableCell>
                            <TableCell width={10}>
                                <h3>#</h3>
                            </TableCell>
                            <TableCell className={"table-cell-name"}>
                                <h3>Content</h3>
                            </TableCell>
                            {hasPermission(
                                PERMISSIONS.ingestion,
                                ACTION.EDIT
                            ) && (
                                <TableCell width={100} align="right">
                                    <h3>Actions</h3>
                                </TableCell>
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredS3ObjectList &&
                            filteredS3ObjectList.map((row: any, index: number) => (
                                <TableRow
                                    key={row.actualKey || row.name || index}
                                    sx={{
                                        "& .MuiTableCell-root": {
                                            color:
                                                row.fileStatus === "restored"
                                                    ? FILE_STATUS_COLORS.RESTORED
                                                    : row.fileStatus ===
                                                      "restore-in-progress"
                                                    ? FILE_STATUS_COLORS.RESTORE_IN_PROGRESS
                                                    : row.fileStatus ===
                                                      "archived"
                                                    ? FILE_STATUS_COLORS.ARCHIVED
                                                    : "inherit",
                                        },
                                        "&:last-child td, &:last-child th": {
                                            border: 0,
                                        },
                                    }}
                                >
                                    {!row.name.endsWith("/") &&
                                    row.fileStatus !== "restore-in-progress" ? (
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={allSelectedKeys.some(
                                                    (k) =>
                                                        k.key === row.actualKey
                                                )}
                                                onChange={handleSelectOne(row)}
                                            />
                                        </TableCell>
                                    ) : (
                                        // keep the column alignment
                                        <TableCell padding="checkbox" />
                                    )}
                                    <TableCell
                                        width={10}
                                        className="table-cell-index"
                                        component="th"
                                        scope="row"
                                    >
                                        <h5>{index + 1}</h5>
                                    </TableCell>
                                    {/* <TableCell onClick={() => onClickFolder(row)} className={'cursor-pointer table-cell'} width={400}>{(row?.name.length > 50) ? `${row?.name.slice(0, 20)}...${row?.name.slice(row?.name.length - 30)}` : row?.name}</TableCell> */}
                                    <TableCell
                                        onClick={() => onClickFolder(row)}
                                        className={
                                            "cursor-pointer table-cell-name"
                                        }
                                        width={400}
                                    >
                                        <Tooltip title={row?.name}>
                                            <span> {row?.name}</span>
                                        </Tooltip>
                                    </TableCell>
                                    {/* <TableCell className='table-cell' >{row?.type}</TableCell>
                                <TableCell className='table-cell' >{row?.size || "-"}</TableCell> */}
                                    {hasPermission(
                                        PERMISSIONS.ingestion,
                                        ACTION.EDIT
                                    ) && (
                                        <TableCell
                                            align="right"
                                            width={100}
                                            className="table-cell"
                                        >
                                            {row.fileStatus !== "archived" &&
                                                row.fileStatus !==
                                                    "restore-in-progress" && (
                                                    <div className="d-flex j-end">
                                                        {row?.name.includes(
                                                            ".xls"
                                                        ) &&
                                                            !row?.name.includes(
                                                                "EMA"
                                                            ) && (
                                                                <>
                                                                    <Button
                                                                        disabled={
                                                                            validateLoader
                                                                        }
                                                                        className="button-pd-s"
                                                                        onClick={() =>
                                                                            validateExcel(
                                                                                row
                                                                            )
                                                                        }
                                                                        variant="outlined"
                                                                    >
                                                                        {validateLoader &&
                                                                            row?.name ===
                                                                                selectedFile && (
                                                                                <CircularProgress
                                                                                    size={
                                                                                        20
                                                                                    }
                                                                                    color="primary"
                                                                                />
                                                                            )}{" "}
                                                                        Validate
                                                                    </Button>

                                                                    {hasPermission(
                                                                        PERMISSIONS.slate,
                                                                        ACTION.EDIT
                                                                    ) && (
                                                                        <>
                                                                            <Button
                                                                                className="button-pd-s"
                                                                                onClick={() =>
                                                                                    generateXml(
                                                                                        row,
                                                                                        "amazon"
                                                                                    )
                                                                                }
                                                                                variant="outlined"
                                                                            >
                                                                                PVC
                                                                                Package
                                                                            </Button>

                                                                            <Button
                                                                                className="button-pd-s"
                                                                                onClick={() =>
                                                                                    generateXml(
                                                                                        row,
                                                                                        "roku"
                                                                                    )
                                                                                }
                                                                                variant="outlined"
                                                                            >
                                                                                Roku
                                                                                Package
                                                                            </Button>

                                                                            <Button
                                                                                className="button-pd-s"
                                                                                onClick={() =>
                                                                                    generateXml(
                                                                                        row,
                                                                                        "mdu"
                                                                                    )
                                                                                }
                                                                                variant="outlined"
                                                                            >
                                                                                MDU
                                                                                Packages
                                                                            </Button>
                                                                        </>
                                                                    )}
                                                                </>
                                                            )}

                                                        {(row?.name.endsWith(
                                                            ".mp4"
                                                        ) ||
                                                            row?.name.endsWith(
                                                                ".mov"
                                                            ) ||
                                                            row?.name.endsWith(
                                                                ".wav"
                                                            ) ||
                                                            row?.name.endsWith(
                                                                ".m4a"
                                                            )) && (
                                                            <Button
                                                                className="button-pd-s"
                                                                onClick={() =>
                                                                    showMediaInfo(
                                                                        row
                                                                    )
                                                                }
                                                                variant="outlined"
                                                            >
                                                                Media Info
                                                            </Button>
                                                        )}

                                                        {row?.name
                                                            ?.toLowerCase()
                                                            .endsWith(
                                                                ".mov"
                                                            ) && (
                                                            <Button
                                                                className="button-pd-s"
                                                                onClick={() =>
                                                                    openCreateProxy(
                                                                        row
                                                                    )
                                                                }
                                                                variant="outlined"
                                                            >
                                                                Create a Proxy
                                                            </Button>
                                                        )}

                                                        {row?.name.includes(
                                                            "/"
                                                        ) &&
                                                            hasPermission(
                                                                PERMISSIONS.ingestion,
                                                                ACTION.EDIT
                                                            ) && (
                                                                <>
                                                                    <Button
                                                                        className="button-pd-s"
                                                                        onClick={() =>
                                                                            moveToGlacier(
                                                                                row
                                                                            )
                                                                        }
                                                                        variant="outlined"
                                                                    >
                                                                        Move to
                                                                        Glacier
                                                                    </Button>
                                                                    <Button
                                                                        className="button-pd-s"
                                                                        onClick={() =>
                                                                            openRestoreFromGlacier(
                                                                                row
                                                                            )
                                                                        }
                                                                        variant="outlined"
                                                                    >
                                                                        Restore
                                                                        from
                                                                        Glacier
                                                                    </Button>
                                                                </>
                                                            )}
                                                        {[
                                                            ".xlsx",
                                                            ".jpg",
                                                            ".jpeg",
                                                            ".png",
                                                            ".xml",
                                                            ".srt",
                                                            ".txt",
                                                        ].some((ext) =>
                                                            row?.name
                                                                ?.toLowerCase()
                                                                .endsWith(ext)
                                                        ) && (
                                                            <Tooltip title="Download File">
                                                                <Button
                                                                    className="button-pd-s"
                                                                    onClick={() =>
                                                                        downloadFile(
                                                                            row
                                                                        )
                                                                    }
                                                                    variant="outlined"
                                                                >
                                                                    <DownloadIcon />
                                                                </Button>
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {nextToken && (
                <div className="load-more">
                    <Button onClick={loadMore} variant="outlined">
                        {loadMoreLoader ? (
                            <CircularProgress size={20} color="primary" />
                        ) : (
                            <ArrowDownwardIcon />
                        )}
                        Load More
                    </Button>
                </div>
            )}

            {validationErrors && (
                <GeneralInfoModal
                    title={
                        <div className="d-flex j-space w-95">
                            Validation Errors
                            <CSVLink
                                headers={ERROR_HEADERS_CSV}
                                filename={selectedFile}
                                data={errorMessagFormattingForCSV(
                                    validationErrors.errors
                                )}
                                separator={";"}
                            >
                                <span className="download-btn">Download</span>
                            </CSVLink>
                        </div>
                    }
                    isOpen={isValidatePopupOpen}
                    handleClose={ingestionValidatePopupClose}
                >
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell width={100}>Row no</TableCell>
                                <TableCell align="left" width={300}>
                                    Error Message
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {validationErrors?.errors.map((row: any) => (
                                <TableRow
                                    key={row.rowNo}
                                    sx={{
                                        "&:last-child td, &:last-child th": {
                                            border: 0,
                                        },
                                    }}
                                >
                                    <TableCell
                                        width={100}
                                        component="th"
                                        scope="row"
                                    >
                                        {row.rowNo}
                                    </TableCell>
                                    <TableCell width={300} align="left">
                                        <ul>
                                            {row?.errorMessage &&
                                                row?.errorMessage.map(
                                                    (
                                                        errorMessage: string,
                                                        index: number
                                                    ) => (
                                                        <li key={index}>
                                                            {errorMessage}
                                                        </li>
                                                    )
                                                )}
                                        </ul>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </GeneralInfoModal>
            )}

            <Dialog
                open={mediaInfoDialogOpen}
                onClose={() => setMediaInfoDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Media Info Output</DialogTitle>
                <DialogContent dividers>
                    <pre
                        style={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            maxHeight: "400px",
                            overflow: "auto",
                        }}
                    >
                        {typeof mediaInfoData === "string"
                            ? mediaInfoData
                            : JSON.stringify(mediaInfoData, null, 2)}
                    </pre>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            const blob = new Blob(
                                [
                                    typeof mediaInfoData === "string"
                                        ? mediaInfoData
                                        : JSON.stringify(
                                              mediaInfoData,
                                              null,
                                              2
                                          ),
                                ],
                                { type: "application/json" }
                            );
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `${
                                selectedFile?.split("/").pop() || "media-info"
                            }.json`;
                            a.click();
                            URL.revokeObjectURL(url);
                        }}
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                    >
                        Download JSON
                    </Button>
                    <Button
                        onClick={() => setMediaInfoDialogOpen(false)}
                        color="secondary"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {waivableErrorsData && (
                <WaivableErrorsDialog
                    isOpen={waivableErrorsDialogOpen}
                    errors={waivableErrorsData.errors}
                    action={waivableErrorsData.action}
                    onClose={handleCloseWaivableErrorsDialog}
                    onWaive={handleWaiveErrors}
                />
            )}

            <CreateProxyDialog
                open={proxyDialogOpen}
                fileName={proxyTargetRow?.name || ""}
                hd={proxyHd}
                submitting={proxySubmitting}
                onHdChange={setProxyHd}
                onClose={closeCreateProxy}
                onProceed={createProxyAPI}
            />
        </div>
    );
};

export default BrowseS3List;
